// ===========================================
// Subscriptions API Functions
// ===========================================
// API calls for subscription management endpoints

import { apiClient } from '../services/api';
import { PaymentPlan } from './paymentPlans.api';

// ===========================================
// TYPES
// ===========================================

export type SubscriptionStatus =
  | 'ACTIVE'
  | 'PAUSED'
  | 'PAST_DUE'
  | 'UNPAID'
  | 'CANCELLED'
  | 'COMPLETED';

export interface SubscriptionFamily {
  id: string;
  name: string;
  parents?: Array<{
    id: string;
    User: {
      firstName: string;
      lastName: string;
      email: string;
    };
  }>;
}

export interface SubscriptionTerm {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface Subscription {
  id: string;
  schoolId: string;
  familyId: string;
  paymentPlanId: string;
  termId: string | null;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  stripePriceId: string | null;
  amount: string;
  currency: string;
  billingDay: number | null;
  startDate: string;
  endDate: string | null;
  nextBillingDate: string | null;
  cancelledAt: string | null;
  pausedAt: string | null;
  status: SubscriptionStatus;
  createdAt: string;
  updatedAt: string;
  family: SubscriptionFamily;
  paymentPlan: PaymentPlan | null;
  term: SubscriptionTerm | null;
  invoices?: Array<{
    id: string;
    invoiceNumber: string;
    status: string;
    total: string;
    dueDate: string;
  }>;
}

export interface SubscriptionFilters {
  familyId?: string;
  paymentPlanId?: string;
  termId?: string;
  status?: SubscriptionStatus;
}

export interface CreateSubscriptionInput {
  familyId: string;
  paymentPlanId: string;
  termId?: string;
  amount: number;
  billingDay?: number;
  startDate?: string;
  endDate?: string;
  paymentMethodId?: string; // For Stripe subscriptions
}

export interface PauseSubscriptionInput {
  reason?: string;
  resumeAt?: string;
}

export interface CancelSubscriptionInput {
  cancelImmediately?: boolean;
  reason?: string;
}

export interface ChangePaymentPlanInput {
  paymentPlanId: string;
  prorate?: boolean;
}

export interface SetupIntentResult {
  clientSecret: string;
  customerId: string;
}

// ===========================================
// API RESPONSE TYPE
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

interface PaginatedResponse<T> {
  subscriptions: T[];
  total: number;
}

// ===========================================
// ADMIN - SUBSCRIPTIONS API
// ===========================================

export const adminSubscriptionsApi = {
  /**
   * Get all subscriptions for the school
   */
  getAll: async (filters?: SubscriptionFilters): Promise<PaginatedResponse<Subscription>> => {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Subscription>>>(
      '/admin/subscriptions',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get a single subscription by ID
   */
  getById: async (id: string): Promise<Subscription> => {
    const response = await apiClient.get<ApiResponse<Subscription>>(
      `/admin/subscriptions/${id}`
    );
    return response.data;
  },

  /**
   * Get all subscriptions for a family
   */
  getByFamily: async (familyId: string): Promise<PaginatedResponse<Subscription>> => {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Subscription>>>(
      `/admin/subscriptions/family/${familyId}`
    );
    return response.data;
  },

  /**
   * Create a new subscription
   */
  create: async (data: CreateSubscriptionInput): Promise<Subscription> => {
    const response = await apiClient.post<ApiResponse<Subscription>>(
      '/admin/subscriptions',
      data
    );
    return response.data;
  },

  /**
   * Pause a subscription
   */
  pause: async (id: string, data?: PauseSubscriptionInput): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      `/admin/subscriptions/${id}/pause`,
      data || {}
    );
    return response.data;
  },

  /**
   * Resume a paused subscription
   */
  resume: async (id: string): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      `/admin/subscriptions/${id}/resume`
    );
    return response.data;
  },

  /**
   * Cancel a subscription
   */
  cancel: async (id: string, data?: CancelSubscriptionInput): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      `/admin/subscriptions/${id}/cancel`,
      data || { cancelImmediately: false }
    );
    return response.data;
  },

  /**
   * Change the payment plan for a subscription
   */
  changePaymentPlan: async (id: string, data: ChangePaymentPlanInput): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      `/admin/subscriptions/${id}/payment-plan`,
      data
    );
    return response.data;
  },
};

// ===========================================
// PARENT - SUBSCRIPTIONS API
// ===========================================

export const parentSubscriptionsApi = {
  /**
   * Get subscriptions for the parent's family
   */
  getAll: async (): Promise<PaginatedResponse<Subscription>> => {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Subscription>>>(
      '/parent/subscriptions'
    );
    return response.data;
  },

  /**
   * Get a specific subscription
   */
  getById: async (id: string): Promise<Subscription> => {
    const response = await apiClient.get<ApiResponse<Subscription>>(
      `/parent/subscriptions/${id}`
    );
    return response.data;
  },

  /**
   * Create a subscription
   */
  create: async (data: Omit<CreateSubscriptionInput, 'familyId'>): Promise<Subscription> => {
    const response = await apiClient.post<ApiResponse<Subscription>>(
      '/parent/subscriptions',
      data
    );
    return response.data;
  },

  /**
   * Pause own subscription
   */
  pause: async (id: string, data?: PauseSubscriptionInput): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      `/parent/subscriptions/${id}/pause`,
      data || {}
    );
    return response.data;
  },

  /**
   * Resume own subscription
   */
  resume: async (id: string): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      `/parent/subscriptions/${id}/resume`
    );
    return response.data;
  },

  /**
   * Cancel own subscription
   */
  cancel: async (id: string, data?: CancelSubscriptionInput): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      `/parent/subscriptions/${id}/cancel`,
      data || { cancelImmediately: false }
    );
    return response.data;
  },

  /**
   * Update payment method for a subscription
   */
  updatePaymentMethod: async (id: string, paymentMethodId: string): Promise<Subscription> => {
    const response = await apiClient.put<ApiResponse<Subscription>>(
      `/parent/subscriptions/${id}/payment-method`,
      { paymentMethodId }
    );
    return response.data;
  },

  /**
   * Create a SetupIntent to add a payment method
   */
  createSetupIntent: async (): Promise<SetupIntentResult> => {
    const response = await apiClient.post<ApiResponse<SetupIntentResult>>(
      '/parent/subscriptions/setup-intent'
    );
    return response.data;
  },
};
